<template>
  <div class="group relative bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all duration-300 overflow-hidden">
    <!-- Loading State -->
    <div v-if="loading" class="p-4 sm:p-6">
      <div class="animate-pulse">
        <!-- Skeleton Header -->
        <div class="flex items-start justify-between mb-4">
          <div class="flex items-start space-x-3 flex-1">
            <div class="w-1 h-12 bg-gray-300 rounded-full flex-shrink-0"></div>
            <div class="flex-1 space-y-2">
              <div class="h-5 bg-gray-300 rounded w-3/4"></div>
              <div class="h-3 bg-gray-300 rounded w-1/2"></div>
            </div>
          </div>
          <div class="w-10 h-10 bg-gray-300 rounded-full flex-shrink-0"></div>
        </div>
        
        <!-- Skeleton Content -->
        <div class="space-y-3 mt-4">
          <div v-for="n in 6" :key="n" class="flex space-x-3">
            <div class="h-3 bg-gray-300 rounded w-1/4 flex-shrink-0"></div>
            <div class="h-3 bg-gray-300 rounded flex-1"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Actual Content -->
    <div v-else class="relative">
      <!-- Header -->
      <div class="p-4 sm:p-6 pb-4">
        <div class="flex items-start justify-between">
          <div class="flex items-start space-x-3 flex-1 min-w-0">
            <!-- Color Indicator -->
            <div class="w-1 h-12 bg-[#4664b2] rounded-full flex-shrink-0"></div>
            
            <!-- Title and Description Preview -->
            <div class="flex-1 min-w-0">
              <h3 class="text-base sm:text-lg font-semibold text-gray-900 leading-tight mb-2 pr-16">
                <span class="line-clamp-2">{{ localizedName }}</span>
              </h3>
              <p v-if="localizedDescription && !expanded" class="text-sm text-gray-600 line-clamp-2">
                {{ localizedDescription }}
              </p>
            </div>
          </div>

          <!-- Expand Button -->
          <button
            @click="$emit('toggleExpand')"
            class="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2C5BA1]"
            :class="expandButtonClass"
            :aria-label="expanded ? $t('collapse') : $t('expand')"
            :aria-expanded="expanded">
            <svg class="w-5 h-5 transition-transform duration-200" 
                 :class="{ 'rotate-45': expanded }"
                 fill="none" 
                 stroke="currentColor" 
                 viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Expanded Content -->
      <transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="opacity-0 max-h-0"
        enter-to-class="opacity-100 max-h-[32rem]"
        leave-active-class="transition-all duration-300 ease-in"
        leave-from-class="opacity-100 max-h-[32rem]"
        leave-to-class="opacity-0 max-h-0">
        <div v-if="expanded" class="border-t border-gray-100 bg-gray-50/50">
          <div class="p-4 sm:p-6 pt-4">
            <dl class="grid grid-cols-1 gap-4 sm:gap-5">
              <!-- Contact Information Section -->
              <div class="space-y-3">
                <h4  class="text-sm font-semibold text-gray-900 uppercase tracking-wide border-b border-gray-200 pb-2">
                  {{ $t('contact_information') }}
                </h4>
                
                <!-- Phone -->
                <div v-if="element.phone" class="flex flex-col sm:flex-row sm:items-center">
                  <dt class="text-sm font-medium text-gray-700 mb-1 sm:mb-0 sm:w-1/3 flex-shrink-0">
                    {{ $t('phone') }}:
                  </dt>
                  <dd class="text-sm text-gray-900 sm:w-2/3">
                    <span class="inline-flex items-center">
                      <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                      </svg>
                      <a :href="`tel:${element.phone}`" class="text-[#4664b2] hover:text-blue-800 transition-colors font-medium">
                        {{ element.phone }}
                      </a>
                    </span>
                  </dd>
                </div>

                <!-- Fax -->
                <div v-if="element.fax" class="flex flex-col sm:flex-row sm:items-center">
                  <dt class="text-sm font-medium text-gray-700 mb-1 sm:mb-0 sm:w-1/3 flex-shrink-0">
                    {{ $t('fax') }}:
                  </dt>
                  <dd class="text-sm text-gray-900 sm:w-2/3">
                    <span class="inline-flex items-center">
                      <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4V2a1 1 0 011-1h4a1 1 0 011 1v2h4a1 1 0 011 1v4a1 1 0 01-1 1h-1v9a2 2 0 01-2 2H6a2 2 0 01-2-2V10H3a1 1 0 01-1-1V5a1 1 0 011-1h4z"/>
                      </svg>
                      <span class="font-medium">{{ element.fax }}</span>
                    </span>
                  </dd>
                </div>

                <!-- Email -->
                <div v-if="element.email" class="flex flex-col sm:flex-row sm:items-center">
                  <dt class="text-sm font-medium text-gray-700 mb-1 sm:mb-0 sm:w-1/3 flex-shrink-0">
                    {{ $t('email') }}:
                  </dt>
                  <dd class="text-sm text-gray-900 sm:w-2/3">
                    <span class="inline-flex items-center">
                      <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                      <a :href="`mailto:${element.email}`" 
                         class="text-[#4664b2] hover:text-blue-800 transition-colors font-medium break-all"
                         :aria-label="`${$t('send_email_to')} ${element.email}`">
                        {{ element.email }}
                      </a>
                    </span>
                  </dd>
                </div>

                <!-- Website -->
                <div v-if="element.website" class="flex flex-col sm:flex-row sm:items-center">
                  <dt class="text-sm font-medium text-gray-700 mb-1 sm:mb-0 sm:w-1/3 flex-shrink-0">
                    {{ $t('website') }}:
                  </dt>
                  <dd class="text-sm text-gray-900 sm:w-2/3">
                    <span class="inline-flex items-center">
                      <svg class="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                      </svg>
                      <a :href="formatWebsiteUrl(element.website)" 
                         target="_blank" 
                         rel="noopener noreferrer"
                         class="text-[#4664b2] hover:text-blue-800 transition-colors font-medium break-all"
                         :aria-label="`${$t('visit_website')} ${element.website}`">
                        {{ element.website }}
                      </a>
                    </span>
                  </dd>
                </div>
              </div>

              <!-- Address Section -->
              <div  class="space-y-3">
                
                <div class="flex flex-col sm:flex-row sm:items-start">
                  <dt class="text-sm font-medium text-gray-700 mb-1 sm:mb-0 sm:w-1/3 flex-shrink-0">
                    {{ $t('address') }}:
                  </dt>
                  <dd class="text-sm text-gray-900 sm:w-2/3">
                    <span class="inline-flex items-start">
                      <svg class="w-4 h-4 mr-2 mt-0.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                      <span class="break-words leading-relaxed">{{ localizedAddress }}</span>
                    </span>
                  </dd>
                </div>
              </div>

              <!-- Description Section -->
              <div v-if="localizedDescription" class="space-y-3">
                <h4 class="text-sm font-semibold text-gray-900 uppercase tracking-wide border-b border-gray-200 pb-2">
                  {{ $t('description') }}
                </h4>
                
                <div class="flex flex-col">
                  <dd class="text-sm text-gray-700 leading-relaxed">
                    <span class="inline-flex items-start">
                      <svg class="w-4 h-4 mr-2 mt-0.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                      <span class="break-words">{{ localizedDescription }}</span>
                    </span>
                  </dd>
                </div>
              </div>
            </dl>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImprovedAnnuaireCard',
  props: {
    element: {
      type: Object,
      required: true
    },
    expanded: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggleExpand'],
  data() {
    return {
      loading: false, // Set to true to test loading state
      currentLanguage: this.$i18n.locale,
    };
  },
  computed: {
    // Get localized text based on current language
    localizedText() {
      return (baseKey) => {
        if (!this.element || !baseKey) return '';
        
        const langSuffix = this.currentLanguage === 'fr' ? '' : `_${this.currentLanguage}`;
        const key = `${baseKey}${langSuffix}`;
        
        return this.element[key] || this.element[baseKey] || '';
      };
    },

    // Get localized name
    localizedName() {
      return this.localizedText('name');
    },

    // Get localized address
    localizedAddress() {
      return this.localizedText('address');
    },

    // Get localized description
    localizedDescription() {
      return this.localizedText('description');
    },

    // Expand button styling
    expandButtonClass() {
      return this.expanded 
        ? 'bg-gradient-to-r from-[#2FB784] to-[#4664b2] hover:from-[#4664b2] hover:to-[#2FB784] group/button transition-all duration-300 transform hover:scale-105 text-white shadow-md' 
        : 'bg-gray-100 text-[#4664b2] hover:bg-gray-200 group-hover:bg-blue-[#C9F2C7] group-hover:text-[#4664b2]';
    }
  },
  watch: {
    "$i18n.locale"(newLocale) {
      this.currentLanguage = newLocale;
    },
  },
  methods: {
    // Format website URL to ensure it has protocol
    formatWebsiteUrl(url) {
      if (!url) return '';
      
      // If URL doesn't start with http:// or https://, add https://
      if (!url.match(/^https?:\/\//)) {
        return `https://${url}`;
      }
      
      return url;
    }
  },
  mounted() {
    // Simulate loading if needed
    if (this.loading) {
      setTimeout(() => {
        this.loading = false;
      }, 1000);
    }
  },
};
</script>
